import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import Toast from '../../components/ui/Toast';
import { knowledgeHubService } from '../../services/knowledgeHubService';

const CreateArticle = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    content_type: 'article',
    status: 'draft',
    author: 'Northstar Analytics',
    author_role: 'Data Strategy Experts',
    author_image: '',
    category: '',
    tags: [],
    image_url: '',
    video_url: '',
    read_time: '',
    publish_date: new Date()?.toISOString()?.split('T')?.[0],
    is_featured: false
  });

  React.useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, userProfile, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        slug: knowledgeHubService?.generateSlug(value)
      }));
    }
  };

  const handleTagsChange = (e) => {
    const tagsArray = e?.target?.value?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag);
    setFormData(prev => ({ ...prev, tags: tagsArray }));
  };

  const handleSave = async (publishStatus) => {
    if (!formData?.title?.trim()) {
      setToast({ message: 'Title is required', type: 'error' });
      return;
    }
    if (!formData?.content?.trim()) {
      setToast({ message: 'Content is required', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      const dataToSave = { ...formData, status: publishStatus };
      await knowledgeHubService?.createContent(dataToSave);
      setToast({ message: publishStatus === 'published' ? 'Article published successfully!' : 'Article saved as draft', type: 'success' });
      setTimeout(() => navigate('/knowledge-hub-admin'), 1200);
    } catch (err) {
      setToast({ message: err?.message || 'Failed to save article', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const wordCount = formData?.content?.trim()?.split(/\s+/)?.filter(w => w)?.length || 0;
  const estimatedReadTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Create Article | Knowledge Hub Admin</title>
      </Helmet>
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/knowledge-hub-admin')}
                className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name="ArrowLeft" size={18} />
                <span className="text-sm font-medium">Back to Admin</span>
              </button>
              <div className="h-5 w-px bg-gray-300" />
              <div>
                <h1 className="text-2xl font-bold text-primary">Create New Article</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Knowledge Hub Content</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => handleSave('draft')}
                disabled={saving}
                iconName="Save"
                iconPosition="left"
              >
                Save Draft
              </Button>
              <Button
                onClick={() => handleSave('published')}
                disabled={saving}
                iconName="Send"
                iconPosition="left"
              >
                {saving ? 'Publishing...' : 'Publish'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-6">

              {/* Title & Slug */}
              <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Article Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="title"
                      value={formData?.title}
                      onChange={handleInputChange}
                      placeholder="Enter a compelling article title..."
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">URL Slug</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">/article/</span>
                      <Input
                        type="text"
                        name="slug"
                        value={formData?.slug}
                        onChange={handleInputChange}
                        placeholder="url-friendly-slug"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Excerpt</label>
                    <textarea
                      name="excerpt"
                      value={formData?.excerpt}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                      placeholder="A brief summary that appears in article listings and search results..."
                    />
                  </div>
                </div>
              </div>

              {/* Content Editor */}
              <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Content <span className="text-red-500">*</span></h2>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{wordCount} words</span>
                    <span>~{estimatedReadTime} min read</span>
                  </div>
                </div>
                <textarea
                  name="content"
                  value={formData?.content}
                  onChange={handleInputChange}
                  rows={20}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm resize-y"
                  placeholder="Write your article content here. Supports markdown formatting.

# Heading 1
## Heading 2

**Bold text** and *italic text*

- Bullet point
- Another point

> Blockquote for important callouts"
                />
                <p className="text-xs text-muted-foreground mt-2">Supports Markdown formatting</p>
              </div>

            </div>

            {/* Sidebar Settings */}
            <div className="space-y-6">

              {/* Publish Settings */}
              <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Publish Settings</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                    <Select
                      name="status"
                      value={formData?.status}
                      onChange={handleInputChange}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Content Type</label>
                    <Select
                      name="content_type"
                      value={formData?.content_type}
                      onChange={handleInputChange}
                    >
                      <option value="article">Article</option>
                      <option value="video">Video</option>
                      <option value="newsletter">Newsletter</option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Publish Date</label>
                    <Input
                      type="date"
                      name="publish_date"
                      value={formData?.publish_date}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Read Time</label>
                    <Input
                      type="text"
                      name="read_time"
                      value={formData?.read_time || `${estimatedReadTime} min read`}
                      onChange={handleInputChange}
                      placeholder={`${estimatedReadTime} min read`}
                    />
                  </div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData?.is_featured}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <div>
                      <span className="text-sm font-medium text-foreground">Featured Article</span>
                      <p className="text-xs text-muted-foreground">Show prominently on Knowledge Hub</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Categorisation */}
              <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Categorisation</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                    <Input
                      type="text"
                      name="category"
                      value={formData?.category}
                      onChange={handleInputChange}
                      placeholder="e.g., Data Governance"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Tags</label>
                    <Input
                      type="text"
                      value={formData?.tags?.join(', ')}
                      onChange={handleTagsChange}
                      placeholder="Governance, AI, Strategy"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Separate tags with commas</p>
                  </div>
                </div>
              </div>

              {/* Author */}
              <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Author</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                    <Input
                      type="text"
                      name="author"
                      value={formData?.author}
                      onChange={handleInputChange}
                      placeholder="Author name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Role / Title</label>
                    <Input
                      type="text"
                      name="author_role"
                      value={formData?.author_role}
                      onChange={handleInputChange}
                      placeholder="e.g., Data Strategy Expert"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Author Image URL</label>
                    <Input
                      type="url"
                      name="author_image"
                      value={formData?.author_image}
                      onChange={handleInputChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Media */}
              <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Media</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Cover Image URL</label>
                    <Input
                      type="url"
                      name="image_url"
                      value={formData?.image_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                    />
                    {formData?.image_url && (
                      <img
                        src={formData?.image_url}
                        alt="Cover preview"
                        className="mt-3 w-full h-32 object-cover rounded-lg border border-gray-200"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  {formData?.content_type === 'video' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Video URL</label>
                      <Input
                        type="url"
                        name="video_url"
                        value={formData?.video_url}
                        onChange={handleInputChange}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons (mobile-friendly bottom) */}
              <div className="flex flex-col space-y-3 lg:hidden">
                <Button
                  onClick={() => handleSave('published')}
                  disabled={saving}
                  iconName="Send"
                  iconPosition="left"
                  className="w-full"
                >
                  {saving ? 'Publishing...' : 'Publish Article'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSave('draft')}
                  disabled={saving}
                  iconName="Save"
                  iconPosition="left"
                  className="w-full"
                >
                  Save as Draft
                </Button>
              </div>

            </div>
          </div>
        </div>
      </main>

      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CreateArticle;
